import React from 'react';
import {useNavigate} from "react-router";
import {useReportStore} from "../../../../../../features/products/useReportStore";
import {shallow} from 'zustand/shallow';
import {Chip, IconButton, Stack, Step, StepLabel, Stepper, Tooltip, Typography} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import {getValidationStepsForProductType} from '../../constants/validationStepSelectors';

const EMPTY_VALIDATION = Object.freeze({});

const ProductHeader = ({
                           activeStep = 0,
                           onStepChange,
                           showValidationStepper = true,
                           backPath = '/products',
                           productTypeValue = null,
                       }) => {
    const navigate = useNavigate();
    const reportValidation = useReportStore(state => state.validation, shallow);
    const title = useReportStore(state => state.title);
    const safeReportValidation = reportValidation ?? EMPTY_VALIDATION;

    // Use appropriate validation steps based on product type
    const validationSteps = getValidationStepsForProductType(productTypeValue);

    // Compute status per step from reportValidation
    const getStepState = (index) => {
        const entry = safeReportValidation[index];
        if (!entry) return 'pending';
        if (entry.passed === true) return 'passed';
        if (entry.passed === false) return 'failed';
        return 'pending';
    };

    // Check if all validation steps are approved
    const allStepsApproved = validationSteps.every((_, idx) => {
        const entry = safeReportValidation[idx];
        return entry && entry.passed === true;
    });

    return (
        <Stack
            direction={'row'}
            alignItems={'center'}
            boxSizing='border-box'
            p={1}
            bgcolor={'white'}
            spacing={2}
            justifyContent={'space-between'}
        >
            <Stack
                direction={'row'}
                alignItems={'center'}
                boxSizing='border-box'
                bgcolor={'white'}
                spacing={1}
                flexGrow={1}
            >
                <IconButton onClick={navigateBack}><ArrowBackOutlinedIcon/></IconButton>
                <Typography variant="h6" sx={{mr: 4}}>{title}</Typography>
                {allStepsApproved && (
                    <Chip
                        label="Approved"
                        color="success"
                        size="small"
                        icon={<CheckCircleOutlineIcon/>}
                    />
                )}
            </Stack>

            {/* Future: additional validation summary or actions */}
            {showValidationStepper && (
                <Stepper activeStep={activeStep}>
                    {validationSteps.map((step, idx) => {
                        const state = getStepState(idx);
                        const icon = state === 'passed' ?
                            <CheckCircleOutlineIcon color="success"/> : state === 'failed' ?
                                <ErrorOutlineIcon color="error"/> :
                                <HourglassBottomIcon color={idx === activeStep ? 'primary' : 'disabled'}/>;
                        return (
                            <Step key={step.key}>
                                <StepLabel
                                    icon={icon}
                                    onClick={() => onStepChange?.(idx)}
                                >
                                    <Tooltip title={step.instruction} arrow>
                                        <Typography
                                            fontWeight={idx === activeStep ? 'bold' : 'normal'}
                                            style={{cursor: 'pointer'}}>{step.label}</Typography>
                                    </Tooltip>
                                </StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
            )}
        </Stack>
    );

    function navigateBack() {
        if (backPath) {
            navigate(backPath);
        } else {
            navigate(-1);
        }
    }
};

export default ProductHeader;